import { Button, TooltipShortcut } from "@itk/mui-components";
import { Stack, Typography } from "@mui/material";
import { isValidElement, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { applicationActions } from "../application-store";
import { hasValue } from "@itk/typescript-utils";
import { WarningAction } from "./warning-dialog-types";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { GenericDialog } from "src/components/generic-dialog";

const ownActions = {
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions & { warningAction: WarningAction };

const WarningDialogBase = (props: Props) => {
  const { warningAction, setWarningAction } = props;
  const { defaultTranslation = "default" } = warningAction;

  const genericTranslationsT = useGenericTranslationsT();

  const onConfirm = useCallback(() => {
    warningAction.confirm();
  }, [warningAction]);

  const onDecline = useMemo(() => {
    switch (warningAction.variant) {
      case "confirmDecline":
      case "confirmDeclineCancel":
        return () => {
          setWarningAction(undefined);
          warningAction.decline?.();
        };
      case "confirmCancel":
      case "confirmOnly":
        return undefined;
      default:
        const assertNever: never = warningAction;
        return assertNever;
    }
  }, [warningAction, setWarningAction]);

  const onCancel = useMemo(() => {
    switch (warningAction.variant) {
      case "confirmDeclineCancel":
      case "confirmCancel":
        return () => {
          setWarningAction(undefined);
          warningAction.cancel?.();
        };
      case "confirmDecline":
      case "confirmOnly":
        return undefined;
      default:
        const assertNever: never = warningAction;
        return assertNever;
    }
  }, [warningAction, setWarningAction]);

  // We are using a switch here to easily add new default translations later
  const message = useMemo(() => {
    if (hasValue(warningAction.dialogProps?.message)) {
      return warningAction.dialogProps?.message;
    }
    switch (defaultTranslation) {
      case "saveFirst":
        return genericTranslationsT.saveFirst;
      case "default":
        return undefined;
      default:
        const assertNever: never = defaultTranslation;
        return assertNever;
    }
  }, [warningAction.dialogProps?.message, defaultTranslation, genericTranslationsT]);

  const messageToRender = useMemo(() => {
    if (typeof message === "string") {
      return (
        <Typography sx={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }} variant="body1" alignSelf="center">
          {message}
        </Typography>
      );
    }
    if (isValidElement(message)) {
      return message;
    }
    return undefined;
  }, [message]);

  const focusButton = warningAction.dialogProps?.focusButton ?? "Confirm";

  // Primary/confirming action always renders rightmost (variant="contained"); every other action
  // (decline, cancel) renders to its left with variant="outlined" — see the Dialog action-button rule
  // in .claude/agent_docs/public-frontend/patterns.md.
  const confirmButton = useMemo(() => {
    const translation = warningAction.dialogProps?.confirmButtonTranslation;
    if (hasValue(translation)) {
      return (
        <TooltipShortcut shortcut={{ action: onConfirm, shortCutKey: "j", modifier: "alt" }}>
          <Button t={translation.t} field={translation.field} k={translation.k} tArgs={translation.tArgs} onClick={onConfirm} variant="contained" autoFocus={focusButton === "Confirm"} />
        </TooltipShortcut>
      );
    }
    return (
      <TooltipShortcut shortcut={{ action: onConfirm, shortCutKey: "j", modifier: "alt" }}>
        <Button t={genericTranslationsT} field="yes" k={genericTranslationsK} onClick={onConfirm} variant="contained" autoFocus={focusButton === "Confirm"} />
      </TooltipShortcut>
    );
  }, [warningAction.dialogProps?.confirmButtonTranslation, genericTranslationsT, onConfirm, focusButton]);

  const declineButton = useMemo(() => {
    if (!hasValue(onDecline)) return undefined;
    const translation = warningAction.dialogProps?.declineButtonTranslation;
    if (hasValue(translation)) {
      return (
        <TooltipShortcut shortcut={{ action: onDecline, shortCutKey: "n", modifier: "alt" }}>
          <Button t={translation.t} field={translation.field} k={translation.k} tArgs={translation.tArgs} onClick={onDecline} variant="outlined" autoFocus={focusButton === "Decline"} />
        </TooltipShortcut>
      );
    }
    return (
      <TooltipShortcut shortcut={{ action: onDecline, shortCutKey: "n", modifier: "alt" }}>
        <Button t={genericTranslationsT} field="no" k={genericTranslationsK} onClick={onDecline} variant="outlined" autoFocus={focusButton === "Decline"} />
      </TooltipShortcut>
    );
  }, [onDecline, warningAction.dialogProps?.declineButtonTranslation, genericTranslationsT, focusButton]);

  const cancelButton = useMemo(() => {
    if (!hasValue(onCancel)) return undefined;
    const translation = warningAction.dialogProps?.cancelButtonTranslation;
    if (hasValue(translation)) {
      return (
        <TooltipShortcut shortcut={{ action: onCancel, shortCutKey: "f", modifier: "alt" }}>
          <Button t={translation.t} field={translation.field} k={translation.k} tArgs={translation.tArgs} onClick={onCancel} variant="outlined" autoFocus={focusButton === "Cancel"} />
        </TooltipShortcut>
      );
    }
    return (
      <TooltipShortcut shortcut={{ action: onCancel, shortCutKey: "f", modifier: "alt" }}>
        <Button t={genericTranslationsT} field="cancel" k={genericTranslationsK} onClick={onCancel} variant="outlined" autoFocus={focusButton === "Cancel"} />
      </TooltipShortcut>
    );
  }, [onCancel, warningAction.dialogProps?.cancelButtonTranslation, genericTranslationsT, focusButton]);

  return (
    <GenericDialog
      onClose={onCancel ?? onDecline ?? (() => undefined)}
      headerTitle={genericTranslationsT.areYouSure}
      renderDialogActions={
        <>
          {cancelButton}
          {declineButton}
          {confirmButton}
        </>
      }
    >
      <Stack width="100%" direction="row" justifyContent="flex-start" alignItems="center">
        {messageToRender}
      </Stack>
    </GenericDialog>
  );
};

export const WarningDialog = connect(undefined, ownActions)(WarningDialogBase);
